import { useTranslation } from 'react-i18next';

import IteratingChip from '@/components/production/tiles/IteratingChip';
import ReworkPendingOverlay from '@/components/production/tiles/ReworkPendingOverlay';
import { cn } from '@/lib/utils';

/**
 * One design reference — a character sheet, a location plate or a hero prop.
 * The three sections of Design References previously repeated this markup verbatim.
 */
export default function DesignTile({
  label,
  badge,
  src,
  onOpen,
  /** A losing best-of-N candidate: greyed out, marked "not selected". */
  decided,
  pendingRework,
  iterating,
  actions,
  children,
}: {
  label: string;
  /** Small mono chip before the name (the authored LOC_nnn anchor). */
  badge?: string;
  src: string;
  onOpen: () => void;
  decided?: boolean;
  pendingRework?: boolean;
  iterating?: boolean;
  /** Hover-revealed controls (Instruct, Options…). */
  actions?: React.ReactNode;
  /** Extra content below the image (e.g. a character's per-view strip). */
  children?: React.ReactNode;
}) {
  const { t } = useTranslation('production');
  return (
    <div className="group relative cursor-pointer" onClick={onOpen}>
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium">
        {badge && (
          <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-primary">{badge}</span>
        )}
        <span className="truncate">{label}</span>
      </div>
      <img src={src} alt={label} className={cn('w-full rounded-lg border border-border', decided && 'opacity-50 grayscale')} />
      {decided && (
        <span className="absolute right-1 top-6 z-20 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-medium text-white">
          {t('design.notSelected')}
        </span>
      )}
      {actions && (
        <div className="absolute left-1 top-6 z-20 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {actions}
        </div>
      )}
      {pendingRework && <ReworkPendingOverlay />}
      {iterating && <IteratingChip />}
      {children}
    </div>
  );
}
